/* eslint-disable */

import {
  GoogleMap,
  Marker,
  Polyline,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import React, { useRef, useState, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from "../utils/config";

export interface LocationPoint {
  lat: number;
  lng: number;
  address?: string;
  label?: string;
  name?: string;
  id?: string;
  type?: "pickup" | "delivery";
}

export interface RouteLine {
  from: LocationPoint;
  to: LocationPoint;
  color?: string;
}

interface PropTypes {
  latt?: number;
  lngg?: number;
  pickupLatt?: number;
  pickupLngg?: number;
  pickups?: LocationPoint[];
  deliveries?: LocationPoint[];
  routes?: RouteLine[];
  mapContainerClassName?: string;
  handleMapClick?: (event: google.maps.MapMouseEvent) => void;
  zoom?: number;
}

const MapContainer = ({ handleMapClick = () => { }, ...props }: PropTypes) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY!,
    libraries: GOOGLE_MAPS_LIBRARIES!,
  });

  const [center, setCenter] = useState({
    lat: props.latt || props.pickupLatt || 0,
    lng: props.lngg || props.pickupLngg || 0,
  });

  const [selectedPoint, setSelectedPoint] = useState<LocationPoint | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Pickups list (from props or single pickup fallback)
  const pickupList: LocationPoint[] =
    props.pickups && props.pickups.length > 0
      ? props.pickups
      : props.pickupLatt && props.pickupLngg
        ? [{ lat: props.pickupLatt, lng: props.pickupLngg, label: "Pickup Location" }]
        : [];

  // Deliveries list (from props or single delivery fallback)
  const deliveryList: LocationPoint[] =
    props.deliveries && props.deliveries.length > 0
      ? props.deliveries
      : props.latt && props.lngg
        ? [{ lat: props.latt, lng: props.lngg, label: "Delivery Location" }]
        : [];

  // Adjust map bounds to show all markers
  const fitBounds = (map: google.maps.Map) => {
    const allPoints = [...pickupList, ...deliveryList];
    if (allPoints.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    allPoints.forEach((p) => {
      if (p.lat && p.lng) {
        bounds.extend({ lat: p.lat, lng: p.lng });
      }
    });

    if (allPoints.length > 1) {
      setCenter({ lat: bounds.getCenter().lat(), lng: bounds.getCenter().lng() });
      map.fitBounds(bounds);
    } else if (allPoints[0]?.lat && allPoints[0]?.lng) {
      setCenter({ lat: allPoints[0].lat, lng: allPoints[0].lng });
      map.setCenter({ lat: allPoints[0].lat, lng: allPoints[0].lng });
    }
  };

  useEffect(() => {
    if (mapRef.current && isLoaded) {
      fitBounds(mapRef.current);
    }
  }, [props.latt, props.lngg, props.pickupLatt, props.pickupLngg, props.pickups, props.deliveries, isLoaded]);

  const handleMapLoaded = (map: google.maps.Map) => {
    mapRef.current = map;
    fitBounds(map);
  };

  const className = props.mapContainerClassName || "";

  return isLoaded ? (
    <GoogleMap
      onLoad={handleMapLoaded}
      center={center}
      zoom={props.zoom || 12}
      onClick={handleMapClick}
      options={{
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      }}
      mapContainerClassName={twMerge(className)}
      mapContainerStyle={{ width: "100%", height: "100%" }}
    >
      {/* Route lines */}
      {props.routes && props.routes.length > 0 ? (
        props.routes.map((route, idx) => (
          <Polyline
            key={`route-${idx}`}
            path={[
              { lat: route.from.lat, lng: route.from.lng },
              { lat: route.to.lat, lng: route.to.lng },
            ]}
            options={{
              strokeColor: route.color || "#3B82F6",
              strokeOpacity: 0.8,
              strokeWeight: 3,
            }}
          />
        ))
      ) : props.latt && props.lngg && props.pickupLatt && props.pickupLngg ? (
        <Polyline
          path={[
            { lat: props.latt, lng: props.lngg },
            { lat: props.pickupLatt, lng: props.pickupLngg },
          ]}
          options={{
            strokeColor: "#3B82F6",
            strokeOpacity: 0.8,
            strokeWeight: 3,
          }}
        />
      ) : null}

      {/* Pickup Markers */}
      {pickupList.map((pickup, idx) => (
        <Marker
          key={`pickup-${idx}`}
          position={{ lat: pickup.lat, lng: pickup.lng }}
          title={pickup.label || pickup.name || pickup.address || "Pickup Location"}
          icon={{
            url: "https://cdn1.iconfinder.com/data/icons/logistics-delivery-set-3/512/29-512.png",
            scaledSize: new window.google.maps.Size(44, 44),
          }}
          onClick={() => setSelectedPoint({ ...pickup, type: "pickup" })}
        />
      ))}

      {/* Delivery Markers */}
      {deliveryList.map((delivery, idx) => (
        <Marker
          key={`delivery-${idx}`}
          position={{ lat: delivery.lat, lng: delivery.lng }}
          title={delivery.label || delivery.name || delivery.address || "Delivery Location"}
          icon={{
            url: "https://i.pinimg.com/originals/11/d8/11/11d811ec58d9ea2df6f7b28dcd176058.png",
            scaledSize: new window.google.maps.Size(44, 44),
          }}
          onClick={() => setSelectedPoint({ ...delivery, type: "delivery" })}
        />
      ))}

      {/* InfoWindow for selected Marker */}
      {selectedPoint && (
        <InfoWindow
          position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
          onCloseClick={() => setSelectedPoint(null)}
        >
          <div className="p-1 max-w-xs text-xs font-sans text-gray-800">
            <div className="flex items-center gap-1.5 font-bold mb-1">
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${selectedPoint.type === "pickup" ? "bg-amber-500" : "bg-blue-600"
                  }`}
              />
              <span className="capitalize text-gray-900 font-semibold">
                {selectedPoint.type === "pickup" ? "Pickup Location" : "Delivery Location"}
              </span>
            </div>
            {selectedPoint.name && (
              <p className="font-semibold text-gray-800 text-sm mt-0.5">{selectedPoint.name}</p>
            )}
            {selectedPoint.address && (
              <p className="text-gray-600 mt-1 leading-snug">{selectedPoint.address}</p>
            )}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  ) : (
    <p>loading...</p>
  );
};

export default MapContainer;


